import { SiteIds } from './../../../models/sites';
import { AppConfig } from 'src/app/app.config';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { switchMap, catchError, mergeMap } from "rxjs/operators";
import { of } from "rxjs";
import { ChangedPasswordVM } from '../../../models/auth';
import * as RegistrationKiwiSaverActions from './../../registration-kiwisaver/actions/registration-kiwisaver.actions';
import * as RegistrationActions from '../../registration/actions/registration.actions';
import * as UserActions from '../../user/actions/user.actions';
import * as ErrorActions from 'src/app/store/global-notification/actions/global-notification.action';
import * as AuthActions from 'src/app/store/auth/actions/auth.actions';
import * as FeatureFlagsActions from 'src/app/store/feature-flags/actions/feature-flags.actions';
import { AuthService } from 'src/app/services/http/auth.service';
import { Router } from '@angular/router';
import { TokenService } from 'src/app/services/token/token.service';
import { Action } from '@ngrx/store';

@Injectable()
export class AuthEffects {
  constructor(
    private actions$: Actions,
    private authService: AuthService,
    private router: Router,
    private tokenService: TokenService,
    private appConfig: AppConfig
  ) {
  }

  private handleAuthenticationSuccess() : Action[] {
    const siteId = this.appConfig.getEnvironment().siteId;
    if (siteId && (siteId === SiteIds.INVESTNOW || siteId === SiteIds.EVIDENTIAL  || siteId === SiteIds.INVESTNOWCOMBINED)) {
      return this.actionsForKiwisaver(new AuthActions.AuthenticateSuccess(this.authService.hasFalseInception()));
    }
    if (!this.tokenService.hasAssumedUser()) {
      return this.actionsForNoAssumedUser();
    }
    if (this.tokenService.isUserInRelationToMultiplePortfolios()) {
      return this.actionsForSingleOrMultiplePortfolios(new AuthActions.AuthenticateSuccess(this.authService.hasFalseInception()));
    }
    if (this.tokenService.isUserInRelationToSingleNonInvestorEntity()) {
      return this.actionsForSingleOrMultiplePortfolios(new AuthActions.AuthenticateSuccess(this.authService.hasFalseInception()));
    }
    return this.actionsForDefaultCase(new AuthActions.AuthenticateSuccess(this.authService.hasFalseInception()));
  }

  private handleAuthenticationError(err) {
    if (err.error.error === 'invalid_tfa' && err.error.error_description !== 'Unable to generate passcode.') {
      return of(new AuthActions.AuthenticateTFAInit());
    }
    return of(new AuthActions.AuthenticateFail(err.error.error_description));
  }

  authenticate$ = createEffect(() => this.actions$.pipe(
    ofType(AuthActions.AUTHENTICATE),
    switchMap((action: AuthActions.Authenticate) => 
        this.authService.login(action.payload.username, action.payload.password).pipe(
      mergeMap(() => this.handleAuthenticationSuccess()),
      catchError((err) => this.handleAuthenticationError(err))
    ))
  ));

    universalLoginAuthenticate$ = createEffect(() => this.actions$.pipe(
        ofType(AuthActions.UNIVERSAL_LOGIN_AUTHENTICATE),
        switchMap((action: AuthActions.UniversalLoginAuthenticate) => this.authService.onLogin(action.payload.token).pipe(
            mergeMap(() => this.handleAuthenticationSuccess()),
            catchError((err) => this.handleAuthenticationError(err))
        ))
    ));

  cognitoAuthenticate = createEffect(() => this.actions$.pipe(
    ofType(AuthActions.COGNITOAUTHENTICATE),
    switchMap((action: AuthActions.CognitoAuthenticate) =>
        this.authService.onCognitoLogin(action.payload.token).pipe(
      mergeMap(() => this.handleAuthenticationSuccess()),
      catchError((err) => this.handleAuthenticationError(err))
    ))
  ));

  authenticateTFA$ = createEffect(() => this.actions$.pipe(
    ofType(AuthActions.AUTHENTICATE_TFA),
    switchMap((action: AuthActions.AuthenticateTFA) => {
      return this.authService.login(action.payload.username, action.payload.password, action.payload.tfaCode)
        .pipe(
          mergeMap(() => {
            const siteId = this.appConfig.getEnvironment().siteId;
            if (siteId && (siteId === SiteIds.INVESTNOW || siteId === SiteIds.EVIDENTIAL  || siteId === SiteIds.INVESTNOWCOMBINED)) {
              return this.actionsForKiwisaver(new AuthActions.AuthenticateTFASuccess(this.authService.hasFalseInception()));
            }
            if (this.tokenService.isUserInRelationToMultiplePortfolios()) {
              return this.actionsForSingleOrMultiplePortfolios(new AuthActions.AuthenticateTFASuccess(this.authService.hasFalseInception()));
            }
            if (this.tokenService.isUserInRelationToSingleNonInvestorEntity()) {
              return this.actionsForSingleOrMultiplePortfolios(new AuthActions.AuthenticateTFASuccess(this.authService.hasFalseInception()));
            }
            return this.actionsForDefaultCase(new AuthActions.AuthenticateTFASuccess(this.authService.hasFalseInception()));
          }),
          catchError((err) => {
            return of(new AuthActions.AuthenticateTFAFail(err.error.error_description));
          })
        );
    })
  ));

  assumeUser$ = createEffect(() => this.actions$.pipe(
    ofType(AuthActions.ASSUME_USER),
    switchMap((action: AuthActions.AssumeUser) => {
      return this.authService.loginWithAssumedUser(action.payload.userId, action.payload.tfaCode)
        .pipe(
          mergeMap(() => {
            if (this.tokenService.isUserInRelationToMultiplePortfolios()) {
              return this.actionsForSingleOrMultiplePortfolios(new AuthActions.AssumeUserSuccess(this.authService.hasFalseInception()));
            }
            if (this.tokenService.isUserInRelationToSingleNonInvestorEntity()) {
              return this.actionsForSingleOrMultiplePortfolios(new AuthActions.AssumeUserSuccess(this.authService.hasFalseInception()));
            }
            return this.actionsForDefaultCase(new AuthActions.AssumeUserSuccess(this.authService.hasFalseInception()));
          }),
          catchError((err) => {
            if (err.error.error === 'invalid_tfa' && err.error.error_description !== 'Unable to generate passcode.') {
              if (action.payload.tfaCode) {
                // TFA was attempted but the code used was incorrect
                return of(new AuthActions.AuthenticateTFAFail(err.error.error_description));
              }
              // TFA is enabled on the assumed user - proceed to log in with TFA
              return of(new AuthActions.AuthenticateTFAInit());
            }
            return of(new AuthActions.AssumeUserFail(err.error.error_description));
          })
        );
    })
  ));

  getIdentityUsers$ = createEffect(() => this.actions$.pipe(
    ofType(AuthActions.GET_IDENTITY_USERS),
    switchMap(() => {
      return this.authService.getUsers()
        .pipe(
          mergeMap((users) => {
            return of(
              new AuthActions.SetIdentityUsers(users),
              new FeatureFlagsActions.LoadFeatureFlags()
            );
          }),
          catchError((err) => {
            return of(new AuthActions.GetIdentityUsersFail(err.error.error_description));
          })
        );
    })
  ));

  forgotPassword$ = createEffect(() => this.actions$.pipe(
    ofType(AuthActions.REQUEST_RESET),
    switchMap((action: AuthActions.RequestReset) => {
      return this.authService.requestPasswordReset(action.payload)
        .pipe(
          mergeMap(() => {
            return [
              new AuthActions.RequestResetPasswordSuccess()
            ];
          }),
          catchError(() => {
            return of(
              new AuthActions.RequestResetPasswordFail('Reset password failed')
            );
          })
        );
    })
  ));

  changePassword$ = createEffect(() => this.actions$.pipe(
    ofType(AuthActions.CHANGE_PASSWORD),
    switchMap((action: AuthActions.ChangePassword) => {
      return this.authService.changePassword(<ChangedPasswordVM>action.payload)
        .pipe(
          mergeMap(() => {
            return [
              new AuthActions.ChangePasswordSuccess('Password has been changed successfully.'),
            ];
          }),
          catchError((err) => {
            return of(
              new ErrorActions.NotificationErrorAction((err.error) ? err.error.message : err.message),
              new AuthActions.ChangePasswordFail((err.error) ? err.error.message : err.message)
            );
          })
        );
    })
  ));

  resetPassword$ = createEffect(() => this.actions$.pipe(
    ofType(AuthActions.RESET_PASSWORD),
    switchMap((action: AuthActions.ResetPassword) => {
      return this.authService.resetPassword(action.payload)
        .pipe(
          mergeMap(() => {
            this.router.navigate(['/auth', 'signin']);
            return [
              new AuthActions.ResetPasswordSuccess()
            ];
          }),
          catchError((err) => {
            return of(
              new AuthActions.ResetPasswordFail(err.error.message)
            );
          })
        );
    })
  ));

  private actionsForKiwisaver(successAction: Action) {
    return [
      successAction,
      new RegistrationKiwiSaverActions.RegisterKiwiSaverRedirect(),
      new UserActions.GetUserProfile()
    ];
  }

  private actionsForNoAssumedUser() {
    this.router.navigate(['/auth', 'select-account']);
    return [
      new AuthActions.AssumeUserInit()
    ];
  }

  private actionsForSingleOrMultiplePortfolios(successAction: Action) {
    this.router.navigate(['/feature', 'dashboard']);
    return [
      successAction,
      new UserActions.GetUserProfile()
    ];
  }

  private actionsForDefaultCase(successAction: Action) {
    return [
      successAction,
      new RegistrationActions.RegistrationGetStatus(),
      new UserActions.GetUserProfile()
    ];
  }
}
